import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TagManager from 'react-gtm-module';
import BoostModal from '../../../../common/BoostModal';
import PhotoCarousel from '../../../../common/PhotoCarousel/PhotoCarousel';
import { formatAddressString, shareListing, shareOffMarketWillingness } from '../../../../../utils/shareUtils';
import { postBoostRequest } from '../../../../utils/boost';
import type { ListingImageData } from '../../../../../hooks/useListingImageUpload';
import { EditIcon } from '../icons';
import { AddMyPlaceFormState } from '../types';
import { ListingAddressObject } from '../resolveListingBuilding';

const HOUSE_IMG = `${process.env.PUBLIC_URL}/assets/markers/seller/Home.png`;
const JAAKKO_AVATAR = `${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`;

// Group a raw digit string into thousands separated by spaces (e.g. "5590000"
// -> "5 590 000"). Mirrors the helper in ReadyToPublishStep.
const formatThousands = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M8.59 13.51L15.42 17.49M15.41 6.51L8.59 10.49M21 5C21 6.65685 19.6569 8 18 8C16.3431 8 15 6.65685 15 5C15 3.34315 16.3431 2 18 2C19.6569 2 21 3.34315 21 5ZM9 12C9 13.6569 7.65685 15 6 15C4.34315 15 3 13.6569 3 12C3 10.3431 4.34315 9 6 9C7.65685 9 9 10.3431 9 12ZM21 19C21 20.6569 19.6569 22 18 22C16.3431 22 15 20.6569 15 19C15 17.3431 16.3431 16 18 16C19.6569 16 21 17.3431 21 19Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ShareListingStepProps {
  form: AddMyPlaceFormState;
  displayAddress: string;
  previewCity: string;
  displaySize: string;
  displayRooms: string;
  // Photos added during the publish step, shown as a swipeable carousel in the
  // share card's visual (same browsing logic as ListingCard).
  photos?: ListingImageData[];
  // Current user's email, used best-effort for the boost request.
  userEmail?: string;
  // The real off-market willingness created on publish. When present the Share
  // button shares the actual listing link; otherwise it falls back to a generic
  // share (e.g. the not-logged-in placeholder path that creates no listing).
  createdListing?: {
    eventId: string;
    buildingId: string;
    address: ListingAddressObject;
    isMatch: boolean;
  } | null;
  // Jump back to the publish step to tweak the listing.
  onEdit: () => void;
  // Finish the flow (lands on the highlighted map result card).
  onDone: () => void;
}

const ShareListingStep = ({
  form,
  displayAddress,
  previewCity,
  displaySize,
  displayRooms,
  photos,
  userEmail,
  createdListing,
  onEdit,
  onDone,
}: ShareListingStepProps) => {
  const { t } = useTranslation();
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const fallbackBuildingAddress = [displayAddress, previewCity].filter(Boolean).join(', ');
  // Drop the country from the address in the AMP flow (e.g. no trailing
  // ", Finland") — the street + postal city is enough here.
  const createdListingAddress = createdListing
    ? formatAddressString({ ...createdListing.address, country: undefined })
    : '';
  const buildingAddress = createdListingAddress || fallbackBuildingAddress;
  const hasCustomPrice = form.askingPrice.trim() !== '';
  const hasBuyerMatch = createdListing?.isMatch === true;
  // Only show committed photos (drop optimistic/errored uploads) in the card.
  const validPhotos = (photos ?? []).filter(
    (photo) => photo.url && !photo.error && !photo.isUploading,
  );

  const handleShare = async () => {
    if (isSharing) return;
    setIsSharing(true);
    TagManager.dataLayer({
      dataLayer: {
        event: 'amp_listing_shared',
        category: 'User Interaction',
        action: 'Click',
        label: 'Share',
        event_address: buildingAddress,
      },
    });
    try {
      if (createdListing) {
        // Share the real listing link so social crawlers get proper meta tags
        // and a match opens the right conversation context.
        await shareOffMarketWillingness({
          buildingId: createdListing.buildingId,
          eventId: createdListing.eventId,
          isSellInterest: true,
          address: createdListing.address,
          t,
          isMatch: createdListing.isMatch,
        });
      } else {
        await shareListing({ address: buildingAddress, t });
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleBoost = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'amp_listing_boost_clicked',
        category: 'User Interaction',
        action: 'Click',
        label: 'Boost',
        event_address: buildingAddress,
      },
    });
    setShowBoostModal(true);
  };

  const handleBoostConfirm = async () => {
    try {
      await postBoostRequest({
        userEmail,
        eventId: createdListing?.eventId,
        buildingId: createdListing?.buildingId,
        actionType: 'sell',
        buildingAddress,
      });

      TagManager.dataLayer({
        dataLayer: {
          event: 'amp_listing_boost_confirmed',
          category: 'User Interaction',
          action: 'Confirm',
          label: 'Boost Confirmed',
          event_address: buildingAddress,
        },
      });

      setShowBoostModal(false);
      onDone();
    } catch (error) {
      console.error('Error sending boost request:', error);
      throw error;
    }
  };

  return (
    <div className="amp-step amp-step--share">
      <div className="amp-share__scroll">
        <h2 className="amp-share__title">{t('newLandingPage.addMyPlace.share.title')}</h2>
        <p className="amp-share__subtitle">
          {t(hasBuyerMatch
            ? 'newLandingPage.addMyPlace.share.matchSubtitle'
            : 'newLandingPage.addMyPlace.share.subtitle')}
        </p>

        <div className="amp-share__card">
          <PhotoCarousel
            block="amp-share"
            images={validPhotos}
            activeAlt={buildingAddress}
            labels={{
              prev: t('listingCard.prevPhoto', 'Previous photo'),
              next: t('listingCard.nextPhoto', 'Next photo'),
              goTo: (index: number) => t('listingCard.goToPhoto', { index: index + 1 }),
            }}
            badge={(
              <span className="amp-share__new-badge">
                {t('newLandingPage.addMyPlace.result.newBadge')}
              </span>
            )}
            emptyContent={(
              <img src={HOUSE_IMG} alt="" aria-hidden="true" className="amp-share__house" />
            )}
          />
          <div className="amp-share__content">
            <p className="amp-share__address">{buildingAddress}</p>
            <p className="amp-share__facts">
              <span className="amp-share__size">{displaySize}m&sup2;</span>
              <span className="amp-share__facts-dot" aria-hidden="true" />
              <span className="amp-share__rooms">
                {t('newLandingPage.addMyPlace.publish.roomsValue', { rooms: displayRooms })}
              </span>
            </p>
            <div className="amp-share__price-row">
              <div className="amp-share__price-wrap">
                {hasCustomPrice ? (
                  <span className="amp-share__price">
                    {`${formatThousands(form.askingPrice)} \u20AC`}
                  </span>
                ) : (
                  <span className="amp-share__price amp-share__price--estimate">
                    {t('newLandingPage.addMyPlace.publish.estimatingPrice')}
                  </span>
                )}
                {/* The Jaakko avatar marks an estimated price; hide it once the
                    user has set their own asking price. */}
                {!hasCustomPrice && (
                  <img
                    src={JAAKKO_AVATAR}
                    alt=""
                    aria-hidden="true"
                    className="amp-share__price-avatar"
                  />
                )}
              </div>
              <button type="button" className="amp-share__edit" onClick={onEdit}>
                <EditIcon />
                <span>{t('newLandingPage.addMyPlace.share.edit')}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="amp-share__actions">
          <button
            type="button"
            className="amp-share__button amp-share__button--share"
            onClick={handleShare}
            disabled={isSharing}
          >
            {isSharing ? (
              <span className="amp-share__spinner" aria-hidden="true" />
            ) : (
              <>
                <ShareIcon />
                {t('newLandingPage.addMyPlace.share.shareButton')}
              </>
            )}
          </button>

          <button
            type="button"
            className="amp-share__button amp-share__button--boost"
            onClick={handleBoost}
            disabled={isSharing}
          >
            <span className="amp-share__button-main">
              <span className="amp-share__rocket" aria-hidden="true">🚀</span>
              {t('newLandingPage.addMyPlace.share.boostButton')}
            </span>
            <span className="amp-share__button-subtitle">
              {t('newLandingPage.addMyPlace.share.boostSubtitle')}
            </span>
          </button>

          <button
            type="button"
            className="amp-share__button amp-share__button--skip"
            onClick={onDone}
            disabled={isSharing}
          >
            {t('newLandingPage.addMyPlace.share.skipButton')}
          </button>
        </div>
      </div>

      <BoostModal
        isOpen={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        onConfirm={handleBoostConfirm}
        actionType="sell"
        buildingAddress={buildingAddress}
      />
    </div>
  );
};

export default ShareListingStep;
